import React, { useCallback, useState, useEffect } from 'react';
import { Form, message, Checkbox, Select, Empty, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { UserOutlined } from '@ant-design/icons';

const PLATFORM_MAP: Record<number, string> = {
  1: '小红书',
  2: '视频号',
  3: '抖音',
  4: '快手',
  5: 'TikTok',
  6: 'Instagram',
  7: 'Facebook',
  8: 'B站',
  9: '百家号',
};

interface Category {
  categoryId: string;
  name: string;
  description?: string;
  icon?: string;
}

export interface Account {
  accountId: string;
  platform?: string;
  platformType?: number;
  userName?: string;
  filePath?: string;
  status?: number;
  createdAt?: string;
  categoryName?: string;
}

interface AccountTypeFormProps {
  onAccountsSelect?: (accounts: Account[]) => void;
  initialAccounts?: Account[];
  showError?: boolean;
  isRequired?: boolean;
}

const ALL_ACCOUNTS = '__all__';
const UNCATEGORIZED = '__uncategorized__';

export const AccountTypeForm: React.FC<AccountTypeFormProps> = React.memo(
  ({ onAccountsSelect, initialAccounts = [], showError, isRequired = true }) => {
    const { t } = useTranslation();
    const initialCategoryResolved = React.useRef(false);

    const [categories, setCategories] = useState<Category[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    // categoryId -> Set(accountId)
    const [categoryAccountMap, setCategoryAccountMap] = useState<Record<string, Set<string>>>({});
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async () => {
      setLoading(true);
      try {
        // Fetch categories and accounts in parallel
        const [catRes, accRes] = await Promise.all([
          fetch('/v1/social-account/category/list', { credentials: 'include' }),
          fetch('/v1/social-account/account/list', { credentials: 'include' }),
        ]);

        if (catRes.status === 401 || accRes.status === 401) {
          message.error('登录已过期，请重新登录');
          return;
        }
        if (!catRes.ok || !accRes.ok) {
          throw new Error('HTTP error while loading accounts');
        }

        const catData = await catRes.json();
        const accData = await accRes.json();

        const categoryList: Category[] = catData.success ? catData.data || [] : [];
        const accountList: Account[] = accData.success ? accData.data || [] : [];

        setCategories(categoryList);
        setAccounts(accountList);

        // Build categoryId -> Set(accountId) via /category/:id/accounts
        const map: Record<string, Set<string>> = {};
        await Promise.all(
          categoryList.map(async (cat) => {
            try {
              const res = await fetch(`/v1/social-account/category/${cat.categoryId}/accounts`, {
                credentials: 'include',
              });
              if (!res.ok) {
                map[cat.categoryId] = new Set();
                return;
              }
              const data = await res.json();
              const accs: Account[] = data.success ? data.data || [] : [];
              map[cat.categoryId] = new Set(accs.map((a) => a.accountId));
            } catch (_error) {
              map[cat.categoryId] = new Set();
            }
          }),
        );
        setCategoryAccountMap(map);
      } catch (error) {
        console.error('获取账号列表失败:', error);
        message.error('获取账号列表失败');
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchData();
      // The source of truth is the external media-publish service. Keep an open
      // selector up to date while accounts are added or their cookies change.
      const interval = window.setInterval(fetchData, 15_000);
      return () => window.clearInterval(interval);
    }, [fetchData]);

    useEffect(() => {
      const nextAccountIds = initialAccounts.map((account) => account.accountId);
      setSelectedAccountIds((previousAccountIds) => {
        if (
          previousAccountIds.length === nextAccountIds.length &&
          previousAccountIds.every((accountId, index) => accountId === nextAccountIds[index])
        ) {
          return previousAccountIds;
        }
        return nextAccountIds;
      });
    }, [initialAccounts]);

    // Generated workflow plans may already contain accounts resolved from a
    // category or account-name request. Open the matching bucket so the
    // preselection is visible and remains editable.
    useEffect(() => {
      if (
        initialCategoryResolved.current ||
        selectedCategory !== null ||
        initialAccounts.length === 0 ||
        categories.length === 0
      ) {
        return;
      }

      const categoryNames = Array.from(
        new Set(
          initialAccounts
            .map((account) => account.categoryName?.trim())
            .filter((name): name is string => Boolean(name)),
        ),
      );
      const matchingCategory =
        categoryNames.length === 1
          ? categories.find((category) => category.name === categoryNames[0])
          : undefined;

      setSelectedCategory(matchingCategory?.categoryId ?? ALL_ACCOUNTS);
      initialCategoryResolved.current = true;
    }, [initialAccounts, categories, selectedCategory]);

    // Accounts belonging to the currently selected category bucket
    const visibleAccounts = React.useMemo(() => {
      if (selectedCategory === null) return [];
      if (selectedCategory === ALL_ACCOUNTS) return accounts;
      if (selectedCategory === UNCATEGORIZED) {
        // Accounts not present in any category set
        const boundIds = new Set<string>();
        for (const set of Object.values(categoryAccountMap)) {
          for (const id of set) {
            boundIds.add(id);
          }
        }
        return accounts.filter((a) => !boundIds.has(a.accountId));
      }
      const set = categoryAccountMap[selectedCategory];
      if (!set) return [];
      return accounts.filter((a) => set.has(a.accountId));
    }, [selectedCategory, accounts, categoryAccountMap]);

    // Resolve the category name an account belongs to, for display in inputs.
    // Reverse-lookup via categoryAccountMap so it stays correct regardless of
    // which bucket is currently selected.
    const resolveAccountCategoryName = useCallback(
      (accountId: string): string => {
        const uncategorized = t('canvas.workflow.variables.uncategorized') || '未分类';
        for (const cat of categories) {
          if (categoryAccountMap[cat.categoryId]?.has(accountId)) {
            return cat.name || uncategorized;
          }
        }
        return uncategorized;
      },
      [categories, categoryAccountMap, t],
    );

    const emitSelection = useCallback(
      (ids: string[]) => {
        const selected = accounts
          .filter((a) => ids.includes(a.accountId))
          .map((a) => ({ ...a, categoryName: resolveAccountCategoryName(a.accountId) }));
        if (onAccountsSelect) {
          onAccountsSelect(selected);
        }
      },
      [accounts, onAccountsSelect, resolveAccountCategoryName],
    );

    const handleCategoryChange = useCallback((categoryId: string | null) => {
      setSelectedCategory(categoryId);
    }, []);

    const handleAccountSelect = useCallback(
      (accountId: string, checked: boolean) => {
        setSelectedAccountIds((prev) => {
          const next = checked ? [...prev, accountId] : prev.filter((id) => id !== accountId);
          emitSelection(next);
          return next;
        });
      },
      [emitSelection],
    );

    const visibleIds = visibleAccounts.map((a) => a.accountId);
    const visibleSelectedCount = visibleIds.filter((id) => selectedAccountIds.includes(id)).length;
    const isAllSelected =
      visibleAccounts.length > 0 && visibleSelectedCount === visibleAccounts.length;
    const isPartiallySelected =
      visibleSelectedCount > 0 && visibleSelectedCount < visibleAccounts.length;

    const handleSelectAll = useCallback(
      (checked: boolean) => {
        setSelectedAccountIds((prev) => {
          let next: string[];
          if (checked) {
            next = Array.from(new Set([...prev, ...visibleIds]));
          } else {
            next = prev.filter((id) => !visibleIds.includes(id));
          }
          emitSelection(next);
          return next;
        });
      },
      [visibleIds, emitSelection],
    );

    const isCategorySelected = selectedCategory !== null;

    return (
      <>
        <Form.Item
          required={isRequired}
          label={t('canvas.workflow.variables.value') || '变量值'}
          name="value"
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm text-refly-text-0 font-medium">
                {t('canvas.workflow.variables.selectAccountCategory') || '选择账号分类'}
              </div>
              <Select
                placeholder={t('canvas.workflow.variables.selectAccountCategory') || '选择账号分类'}
                value={selectedCategory ?? undefined}
                onChange={(value) => handleCategoryChange(value ?? null)}
                loading={loading}
                style={{ width: '100%' }}
                allowClear
                options={[
                  {
                    label: t('canvas.workflow.variables.allAccounts') || '全部账号',
                    value: ALL_ACCOUNTS,
                  },
                  {
                    label: t('canvas.workflow.variables.uncategorized') || '未分类',
                    value: UNCATEGORIZED,
                  },
                  ...categories.map((cat) => ({
                    label: cat.name,
                    value: cat.categoryId,
                  })),
                ]}
              />
            </div>

            {isCategorySelected && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-refly-text-0 font-medium">
                    {t('canvas.workflow.variables.selectAccounts') || '选择账号'}
                    <span className="text-refly-text-2 ml-1 font-normal">
                      (
                      {t('canvas.workflow.variables.selectedCount', {
                        count: selectedAccountIds.length,
                      }) || `已选 ${selectedAccountIds.length} 个`}
                      )
                    </span>
                  </div>
                </div>

                <div className="max-h-[340px] overflow-hidden border border-refly-Card-Border rounded-lg flex flex-col">
                  <div className="flex-shrink-0 px-3 py-2 bg-refly-bg-control-z0 border-b border-refly-Card-Border">
                    <div className="flex items-center justify-between">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartiallySelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={visibleAccounts.length === 0}
                      >
                        <span className="text-sm text-refly-text-0">
                          {t('canvas.workflow.variables.selectAll') || '全选'}
                        </span>
                      </Checkbox>
                      <span className="text-xs text-refly-text-2">
                        {visibleSelectedCount} / {visibleAccounts.length}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1">
                    <Spin spinning={loading}>
                      {visibleAccounts.length === 0 ? (
                        <div className="p-4">
                          <Empty
                            description={t('canvas.workflow.variables.noAccounts') || '暂无账号'}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          {visibleAccounts.map((account) => (
                            <div
                              key={account.accountId}
                              className={`flex items-center justify-between px-3 py-2 border-b border-refly-Card-Border last:border-b-0 hover:bg-refly-bg-control-z0 cursor-pointer transition-colors ${
                                selectedAccountIds.includes(account.accountId)
                                  ? 'bg-refly-tertiary-hover'
                                  : ''
                              }`}
                              onClick={() => {
                                const isChecked = selectedAccountIds.includes(account.accountId);
                                handleAccountSelect(account.accountId, !isChecked);
                              }}
                            >
                              <Checkbox
                                checked={selectedAccountIds.includes(account.accountId)}
                                onChange={(e) =>
                                  handleAccountSelect(account.accountId, e.target.checked)
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex items-center gap-2 flex-1 ml-2 min-w-0">
                                <span className="text-refly-text-2">
                                  <UserOutlined />
                                </span>
                                <span className="text-sm text-refly-text-0 truncate flex-1">
                                  {account.userName || account.accountId}
                                </span>
                              </div>
                              {account.platformType != null && (
                                <span className="text-xs text-refly-text-2 ml-2 flex-shrink-0">
                                  {PLATFORM_MAP[account.platformType] ||
                                    account.platform ||
                                    account.platformType}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </Spin>
                  </div>
                </div>
              </div>
            )}

            {!isCategorySelected && (
              <div className="p-4 border border-dashed border-refly-Card-Border rounded-lg text-center">
                <Empty
                  description={
                    t('canvas.workflow.variables.selectAccountCategoryFirst') || '请先选择账号分类'
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </div>
        </Form.Item>

        {showError && selectedAccountIds.length === 0 && (
          <div className="text-red-500 text-xs mt-1 mb-2">
            {t('canvas.workflow.variables.selectAccountFirst') || '请先选择账号'}
          </div>
        )}
      </>
    );
  },
);

AccountTypeForm.displayName = 'AccountTypeForm';
